import React, { useEffect, useState } from 'react';
import { getConfig } from '../config';

type Row = {
  workspaceName: string;
  userName: string;
  userRole: string;
  courtNumber: number | null;
  startedAt: string;
  currentMinutes: number;
  recordingId: number;
  recordingUuid: string;
};

type Workspace = { id: number; name: string };

const AnalyticsActiveRecordings: React.FC = () => {
  const { apiBase } = getConfig();
  const [rows, setRows] = useState<Row[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [sort, setSort] = useState<'workspaceName' | 'userName' | 'userRole' | 'courtNumber' | 'startedAt' | 'currentMinutes' | 'recordingId'>('startedAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/superadmin/workspaces/options`, { credentials: 'include' });
        if (!res.ok) throw new Error(`Failed to load workspaces: ${res.status}`);
        const data = (await res.json()) as Workspace[];
        setWorkspaces(data);
      } catch (e: any) {
        console.warn('Workspace list load failed:', e?.message || e);
      }
    })();
  }, [apiBase]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const url = new URL(`${apiBase}/superadmin/analytics/active-recordings`);
        if (workspaceId) url.searchParams.set('workspaceId', workspaceId);
        if (sort) url.searchParams.set('sort', sort);
        if (order) url.searchParams.set('order', order);
        const res = await fetch(url.toString(), { credentials: 'include' });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const data = (await res.json()) as Row[];
        setRows(data);
      } catch (e: any) {
        setErr(e?.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase, workspaceId, sort, order]);

  if (loading) return <div>Loading…</div>;
  if (err) return <div className="text-red-600">Error: {err}</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Active Recordings</h1>
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm">Workspace</label>
        <select
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="">All</option>
          {workspaces.map(w => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white shadow rounded overflow-hidden">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('workspaceName'); setOrder(o => sort==='workspaceName'?(o==='asc'?'desc':'asc'):'asc'); }}>Workspace</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('userName'); setOrder(o => sort==='userName'?(o==='asc'?'desc':'asc'):'asc'); }}>User</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('userRole'); setOrder(o => sort==='userRole'?(o==='asc'?'desc':'asc'):'asc'); }}>Role</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('courtNumber'); setOrder(o => sort==='courtNumber'?(o==='asc'?'desc':'asc'):'asc'); }}>Court</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('startedAt'); setOrder(o => sort==='startedAt'?(o==='asc'?'desc':'asc'):'asc'); }}>Started</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('currentMinutes'); setOrder(o => sort==='currentMinutes'?(o==='asc'?'desc':'asc'):'asc'); }}>Minutes</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('recordingId'); setOrder(o => sort==='recordingId'?(o==='asc'?'desc':'asc'):'asc'); }}>ID</th>
              <th className="text-left p-3">UUID</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={`${r.recordingId}-${idx}`} className="border-b last:border-none">
                <td className="p-3">{r.workspaceName}</td>
                <td className="p-3">{r.userName}</td>
                <td className="p-3">{r.userRole}</td>
                <td className="p-3">{r.courtNumber ?? '-'}</td>
                <td className="p-3">{new Date(r.startedAt).toLocaleString()}</td>
                <td className="p-3">{r.currentMinutes}</td>
                <td className="p-3">{r.recordingId}</td>
                <td className="p-3 font-mono text-xs break-all">{r.recordingUuid}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td className="p-3" colSpan={8}>No active recordings</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AnalyticsActiveRecordings;


